import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link, LinkProps, To, useLocation, useResolvedPath } from 'react-router-dom';

import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { cls } from '@/utils/cls';

type Props = LinkProps & {
  isExternal?: boolean;
};

const FORECASTS_ROOT = RouterConfig[CanvasRoutes.FoundationForecasting].path;
const DECISIONS_ROOT = RouterConfig[CanvasRoutes.Decisions].path;
const DATA_ROOT = RouterConfig[CanvasRoutes.Data].path;

const isInSection = (path: string, root: string) => path === root || path.startsWith(`${root}/`);

const shouldPreserveSearch = (fromPath: string, toPath: string) => {
  // Data sub-pages don't share query state; the ?id detail selector must not survive navigation
  // between Data pages (e.g. customers?id=… → objects). Cross-page links set ?id explicitly via a
  // plain react-router Link (not this component), so they are unaffected.
  if (isInSection(fromPath, DATA_ROOT) && isInSection(toPath, DATA_ROOT)) return false;

  const sectionRoots = [FORECASTS_ROOT, DECISIONS_ROOT];
  const fromSection = sectionRoots.find((root) => isInSection(fromPath, root));
  const toSection = sectionRoots.find((root) => isInSection(toPath, root));

  if (fromSection && toSection && fromSection !== toSection) return false;
  return true;
};

const isAbsoluteUrl = (value: To) => typeof value === 'string' && /^https?:\/\//i.test(value);

export function LinkPreserveSearch({ to, children, target, isExternal = false, className, ...rest }: Props) {
  const { search, pathname } = useLocation();

  const safeTo = to ?? pathname;
  const isAbs = isAbsoluteUrl(safeTo);
  const resolvedPath = useResolvedPath(isAbs ? pathname : safeTo);
  const resolvedPathname = isAbs ? null : resolvedPath.pathname;
  const preserveSearch = resolvedPathname ? shouldPreserveSearch(pathname, resolvedPathname) : true;
  const fallbackSearch = preserveSearch ? search : '';
  const explicitSearch = typeof to === 'object' ? to.search : undefined;

  if (!to) return null;

  const toProp: To =
    typeof to === 'string'
      ? { pathname: to, search: fallbackSearch }
      : { ...to, search: explicitSearch ?? fallbackSearch };
  const externalTarget = isExternal ? '_blank' : target;
  const externalRel = isExternal ? 'noopener noreferrer' : rest.rel;
  const showExternalIcon = externalTarget === '_blank';
  const linkClassName = cls(className, showExternalIcon && 'inline-flex items-center gap-1 align-middle');
  const content = showExternalIcon ? (
    <span className="inline-flex items-center gap-1">
      <span>{children}</span>
      <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
    </span>
  ) : (
    children
  );

  return (
    <Link {...rest} to={toProp} target={externalTarget} rel={externalRel} className={linkClassName}>
      {content}
    </Link>
  );
}
